import { useEffect, useMemo, useState } from 'react';
import {
  Filters,
  SchemaFilterType,
  SelectItemProps,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
} from '@ui/src';
import { useAppDispatch } from '@store';
import queryString from 'query-string';
import {
  newApplicationListColumns,
  newApplicationListFiltersSchema,
  newApplicationListSchema,
} from '../variables/list';
import { useLazyGetNewApplicationsListQuery } from '../../store/services';
import {
  NewApplicationCorporateInfo,
  NewApplicationListItem,
  NewApplicationStatus,
} from '../../types';
import formatNewApplicationsList from '../helpers/format-applications-list';
import { clearNewApplicationInfoState } from '../../individual/store/slices';
import { clearNewApplicationInfoState as clearNewCorporateApplicationState } from '../../corporate/store/slices';
import { clearNewApplicationInfoState as clearNewMRApplicationState } from '../../main-representative/store/slices';
import { clearApplicationsList } from '../../store/slices';

const useList = ({
  newApplications,
  countries,
  newApplicationInfo,
  newCorporateApplicationInfo,
  newMRApplicationInfo,
  userId,
  search,
}: {
  newApplications?: NewApplicationListItem[];
  countries?: SelectItemProps[];
  newApplicationInfo?: NewApplicationListItem;
  newCorporateApplicationInfo?: NewApplicationCorporateInfo;
  newMRApplicationInfo?: NewApplicationListItem;
  userId?: string;
  search?: string;
}) => {
  const [formattedColumns, setFormattedColumns] = useState(
    newApplicationListColumns,
  );

  const [savedFilters, setSavedFilters] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState('');

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    newApplicationListSchema,
  );

  const prevFilters = usePrevious(filters);

  const [getCustomersApplicationsList, { isFetching, isLoading }] =
    useLazyGetNewApplicationsListQuery();

  const { getListAction, onSort, onFilter, setPage, errorList } =
    useGetList<NewApplicationListItem>({
      getList: getCustomersApplicationsList,
      onFilterChange,
      onSortChange,
      schema: newApplicationListSchema,
    });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: newApplicationListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatNewApplicationsList(
      newApplications as NewApplicationListItem[],
    );

    return data;
  }, [newApplications, filters, sort]);

  const onSavedFilterClick = (id: string, savedFilter: Filters) => {
    const filterValues: Filters = {
      ...savedFilter,
      take: { value: ['20'], type: SchemaFilterType.STRING },
    };

    onFilter(filterValues);
    setSelectedSavedFilter(id);
  };

  useEffect(() => {
    setValues('countryId', countries as SelectItemProps[]);
  }, [countries]);

  useEffect(() => {
    dispatch(clearNewApplicationInfoState());
    dispatch(clearNewCorporateApplicationState());
    dispatch(clearNewMRApplicationState());
  }, [newApplicationInfo, newCorporateApplicationInfo, newMRApplicationInfo]);

  useEffect(() => {
    let nextColumns = ([] as any[]).concat(newApplicationListColumns);

    if (filters.type) {
      if (filters.type.value) {
        nextColumns = nextColumns.map((item) => {
          const result = { ...item };

          if (!filters.type.value.includes('CORPORATE')) {
            if (result.name === 'companyName') {
              result.hidden = true;
            }
          } else if (
            filters.type.value.length === 1 &&
            filters.type.value.includes('CORPORATE')
          ) {
            if (
              result.name === 'firstName' ||
              result.name === 'lastName' ||
              result.name === 'phone' ||
              result.name === 'email'
            ) {
              result.hidden = true;
            }
          }

          return result;
        });
      }

      setFormattedColumns(nextColumns);
    } else if (filters.companyName) {
      nextColumns = nextColumns.map((item) => {
        const result = { ...item };

        if (
          result.name === 'firstName' ||
          result.name === 'lastName' ||
          result.name === 'phone' ||
          result.name === 'email'
        ) {
          result.hidden = true;
        }

        return result;
      });

      setFormattedColumns(nextColumns);
    } else {
      setFormattedColumns(newApplicationListColumns);
    }
  }, [filters]);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !newApplications &&
      sort &&
      sort.sortProp
    ) {
      let nextFilters: Filters = {};

      const savedFiltersJSON = localStorage.getItem('applications-filters');

      let parsed: Record<string, any> = {};

      if (search) {
        parsed = queryString.parse(search);

        if (parsed) {
          delete parsed.sortProp;
          delete parsed.sortDir;
        }
      }

      let parsedSavedFilters: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
      }[];

      let defaultSavedFilter;

      if (!Object.keys(filters)?.length) {
        if (savedFiltersJSON) {
          parsedSavedFilters = JSON.parse(savedFiltersJSON);

          if (parsedSavedFilters && parsedSavedFilters?.length) {
            const currentUserParsedFilters = parsedSavedFilters?.filter(
              (item: any) => item.employeeId === userId,
            );

            if (currentUserParsedFilters && currentUserParsedFilters?.length) {
              defaultSavedFilter = currentUserParsedFilters?.find(
                (item) => item.default,
              );
            }
          }
        }

        if (defaultSavedFilter) {
          setSelectedSavedFilter(defaultSavedFilter.id);

          nextFilters = { ...nextFilters, ...defaultSavedFilter?.value };
        } else {
          nextFilters = { ...nextFilters, ...filters };
        }
      } else {
        nextFilters = { ...nextFilters, ...filters };
      }

      if (
        !filters.status &&
        !defaultSavedFilter &&
        !Object.keys(parsed as any).length
      ) {
        nextFilters.status = {
          value: [
            NewApplicationStatus.READY_TO_REVIEW,
            NewApplicationStatus.REVIEW_IN_PROGRESS,
          ],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({ filters: nextFilters, sort });
    }
  }, [filters, sort, newApplications, search]);

  useEffect(() => {
    dispatch(clearApplicationsList());
  }, []);

  useEffect(() => {
    const savedFiltersJSON = localStorage.getItem('applications-filters');

    if (savedFiltersJSON) {
      const parsed = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const currentEmployeeSavedFilters = parsed?.filter(
          (item: any) => item.employeeId === userId,
        );

        setSavedFilters(currentEmployeeSavedFilters);
      }
    }
  }, [userId]);

  return {
    isFetching,
    isLoading,
    formattedColumns,
    formattedData,
    onSort,
    errorList,
    filters,
    sort,
    setSelectedSavedFilter,
    onFilter,
    filtersComponent,
    handleSubmit,
    selectedSavedFilter,
    setPage,
    savedFilters,
    setSavedFilters,
    onSavedFilterClick,
  };
};

export default useList;
